import { QueryClient } from '@tanstack/react-query'
import { STATE_LIST } from '@vangst/lib/constants/locations'
import { fetchPage } from '@vangst/services/oogst/page'
import { RobotsEnum } from '@vangst/services/oogst/types'
import Routes from '@vangst/services/routes'
import { slugify } from '@vangst/services/statesAndCities'
import { InferGetStaticPropsType } from 'next'
import Breadcrumbs from '../../../components/navigation/Breadcrumbs'
import ClickyLink from '../../../components/navigation/ClickyLink'
import Markdown from '../../../components/views/Markdown'
import PageHeader from '../../../components/views/PageHeader'
import Meta from '../../../domains/application/Meta'

export async function getStaticProps() {
  const queryClient = new QueryClient()
  const rawUrl = __filename.split('pages')[1]?.replace('.js', '')
  const { getPage } = await fetchPage(queryClient, { rawUrl })
  const page = {
    ...getPage,
    title: getPage?.title ?? 'Employment, Cannabis Jobs',
  }
  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    { href: Routes.JOBS, text: 'Jobs' },
    { href: null, text: 'Browse Jobs' },
  ]

  return {
    props: { crumbs, page },
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

export default function Browse(props: PropsType) {
  const { crumbs, page } = props

  return (
    <>
      <Meta
        key="meta"
        canonicalUrl={page.metaInfo?.canonicalUrl}
        crumbs={crumbs}
        description={page.metaInfo?.description}
        image={page.pagePhotos?.[0]?.url}
        publishDate={page.postedAt}
        robots={page.metaInfo?.robots ?? RobotsEnum.IndexFollow}
        title={page.metaInfo?.title ?? page.title}
      />
      <Breadcrumbs crumbs={crumbs} />
      <article className="flow-y-md mood-white">
        <PageHeader
          icon="pin drop"
          title={page.title}
          subtitle={page.subtitle}
          description={page.description}
          imgAlt={page.pagePhotos?.[0]?.alt}
          imgSrc={page.pagePhotos?.[0]?.url}
        />
        <section className="contain-xyc flow-y-md">
          <h2 className="heading-md">
            Find Cannabis Employment Information In Your State
          </h2>
          <ul className="grid gap-16 md:grid-cols-2 lg:grid-cols-3">
            {Object.keys(STATE_LIST).map((abbr) => {
              const name = STATE_LIST[abbr as keyof typeof STATE_LIST]
              return (
                <li className="flow-y-xs" key={abbr}>
                  <h3 className="heading-sm">{name}</h3>

                  <ClickyLink
                    className="link-orange max-w-fit underline"
                    href={Routes.REPORTS_STATE_REQUIREMENTS_DETAIL.replace(
                      ':slug',
                      slugify(name),
                    )}
                  >
                    &raquo; View {name} Work Requirements
                  </ClickyLink>
                  <ClickyLink
                    className="link-orange max-w-fit underline"
                    href={`/jobs/browse/us/${abbr.toLowerCase()}`}
                  >
                    &raquo; View {name} Cannabis Jobs
                  </ClickyLink>
                </li>
              )
            })}
          </ul>
        </section>

        {page.markdownContent && (
          <section className="contain-xyc mood-white">
            <div className="text-sm">
              <Markdown renderType="basic">{page.markdownContent}</Markdown>
            </div>
          </section>
        )}
      </article>
    </>
  )
}
